import type { DatePickerProps } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useState } from 'react';

type OnPickerValueChange = NonNullable<DatePickerProps['onPickerValueChange']>;
type PickerValueChangeInfo = Parameters<OnPickerValueChange>[1];
type OnCalendarChange = NonNullable<DatePickerProps['onCalendarChange']>;
type CalendarChangeDate = Parameters<OnCalendarChange>[0];

interface DatePickerPanelProps {
  pickerValue: Dayjs;
  onPickerValueChange: OnPickerValueChange;
  onCalendarChange: OnCalendarChange;
  onOpenChange: (open: boolean) => void;
}

/**
 * Takes ownership of an antd DatePicker's open panel month.
 *
 * rc-picker resets the panel to the selected value's month whenever the
 * identity of `value` changes while the panel is open — see
 * `useRangePickerValue`, whose layout effect ends in
 * `setCurrentPickerValue(next, 'reset')`. That made it impossible to navigate
 * to another month, because a re-render could land at any moment and snap the
 * panel back (OP-2285).
 *
 * Controlling `pickerValue` makes the panel immune to that: rc-picker still
 * fires its reset, but it arrives tagged `source: 'reset'` and is discarded,
 * while the user's own navigation arrives as `source: 'panel'` and is kept.
 * Unlike stabilising `value`, this does not depend on what the server echoes
 * back after an autosave.
 *
 * Discarding every `'reset'` would also stop the panel following a date typed
 * into the field, so `onCalendarChange` — which fires for a typed date but not
 * for a `value` prop change (`useCalendarValue` syncs those through a silent
 * effect) — moves the panel back in step.
 *
 * The panel is released when the popup closes, so the next open starts from
 * the selected date again rather than wherever the user last browsed to. While
 * released, the panel month is rc-picker's own again, which its open layout
 * effect re-derives from the value or `getNow()`.
 *
 * @param value - The picker's current value, used as the panel's start point
 * @returns Props to spread onto the DatePicker. Note this owns `onOpenChange`,
 *   `onCalendarChange` and the `pickerValue` pair — a caller needing its own
 *   must compose them rather than pass them alongside.
 */
export const useDatePickerPanel = (
  value: Dayjs | null,
  fallback?: Dayjs | null
): DatePickerPanelProps => {
  const [panelValue, setPanelValue] = useState<Dayjs | null>(null);

  const onPickerValueChange = useCallback<OnPickerValueChange>(
    (next: Dayjs, info: PickerValueChangeInfo) => {
      if (info.source === 'panel') {
        setPanelValue(next);
      }
    },
    []
  );

  const onCalendarChange = useCallback<OnCalendarChange>(
    (date: CalendarChangeDate) => {
      const next = Array.isArray(date) ? date[0] : date;
      if (next) {
        setPanelValue(next);
      }
    },
    []
  );

  const onOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setPanelValue(null);
    }
  }, []);

  return {
    // Always provide a valid Dayjs — rc-picker destructures pickerValue as an
    // array internally and crashes on null/undefined. Chain of precedence:
    //   1. panelValue — set while user browses months (OP-2285 snap fix)
    //   2. value      — the picker's current selection
    //   3. fallback   — e.g. the input document's Start Date, when no date is
    //                   set on this field yet
    //   4. dayjs()    — today, when neither of the above is available; safe
    //                   because panelValue (non-null while the picker is
    //                   open) shadows this, so the OP-2285 regression cannot
    //                   occur.
    pickerValue: panelValue ?? value ?? fallback ?? dayjs(),
    onPickerValueChange,
    onCalendarChange,
    onOpenChange,
  };
};

export default useDatePickerPanel;
